import {
  claimDealItemsInProgressToast,
  clearDealItemsInProgressToast,
  replaceDealItemsInProgressWithSuccess,
} from '@/lib/deal-items-toast';
import {
  GetDatasourceDocument,
  type GetDatasourceQuery,
} from '@/lib/graphql/generated/graphql';
import { datasourceImportReportState } from '@/states/datasource-import-report.state';
import type { ApolloClient } from '@apollo/client';
import type { TFunction } from 'i18next';
import { getDefaultStore } from 'jotai';

type DealDataSource = NonNullable<
  NonNullable<GetDatasourceQuery['deal']>['dataSources'][number]
>;

const IMPORT_REPORT_DATASOURCE_TYPES = ['CSVDataSource', 'WebsiteDataSource'];

function datasourceCompletedToast(t: TFunction) {
  return {
    title: t('general.success'),
    description: t(
      'components.deal_items_subscription.toast_datasource_completed'
    ),
  };
}

function hasImportReport(dataSource: DealDataSource): boolean {
  return IMPORT_REPORT_DATASOURCE_TYPES.includes(dataSource.__typename ?? '');
}

/**
 * Publie l'état du rapport d'import d'un datasource abouti.
 *
 * Le toast "en cours" n'est pas géré ici : le rapport remplace l'information qu'il
 * portait, donc l'appelant le retire selon ce qu'il en sait.
 */
function openImportReport(dealId: string, dataSource: DealDataSource): void {
  const summary =
    'importSummary' in dataSource && dataSource.importSummary
      ? dataSource.importSummary
      : null;

  getDefaultStore().set(datasourceImportReportState, {
    dealId,
    datasourceId: dataSource.id,
    instructionMode: dataSource.instructions.instructionMode,
    itemsCreatedIds: summary?.itemsCreatedIds ?? [],
    selectedItemsUpdatedIds: summary?.selectedItemsUpdatedIds ?? [],
    excludedItemsUpdatedIds: summary?.excludedItemsUpdatedIds ?? [],
    itemsSkippedIds: [...(summary?.itemsSkippedIds ?? [])],
  });
}

/**
 * Résout le toast "en cours" d'un import abouti : ouvre le rapport d'import pour
 * les sources qui en ont un (CSV, site web), sinon remplace le toast par un
 * succès neutre.
 */
function resolveProcessedDatasource(
  dealId: string,
  dataSource: DealDataSource,
  t: TFunction
): void {
  const datasourceId = dataSource.id;

  if (!hasImportReport(dataSource)) {
    replaceDealItemsInProgressWithSuccess(datasourceCompletedToast(t), {
      datasourceId,
      kind: 'datasourceProcessed',
    });
    return;
  }

  clearDealItemsInProgressToast({
    datasourceId,
    kind: 'datasourceProcessed',
  });
  openImportReport(dealId, dataSource);
}

/**
 * Point d'entrée de la subscription WebSocket `dealItemsUpdatedV3`, qui reçoit le
 * `datasourceId` dans `fromAction`. Relit le datasource pour en tirer le résumé
 * d'import, puis résout le toast correspondant.
 *
 * La relecture est nécessaire parce que `importSummary` n'existe que dans
 * `GetDatasource`. En cas d'échec, on retombe sur un succès neutre plutôt que de
 * laisser le toast "en cours" suspendu.
 */
export async function showImportReportFromProcessedDatasource(
  client: ApolloClient,
  dealId: string,
  datasourceId: string | null | undefined,
  t: TFunction
): Promise<void> {
  try {
    const { data } = await client.query<GetDatasourceQuery>({
      query: GetDatasourceDocument,
      variables: { dealId },
      fetchPolicy: 'network-only',
    });
    const dataSources: DealDataSource[] = data?.deal?.dataSources ?? [];
    const dataSource = datasourceId
      ? dataSources.find((candidate) => candidate.id === datasourceId)
      : undefined;

    if (dataSource) {
      resolveProcessedDatasource(dealId, dataSource, t);
      return;
    }
    if (datasourceId) {
      replaceDealItemsInProgressWithSuccess(datasourceCompletedToast(t), {
        datasourceId,
        kind: 'datasourceProcessed',
      });
    }
  } catch {
    replaceDealItemsInProgressWithSuccess(datasourceCompletedToast(t), {
      datasourceId,
      kind: 'datasourceProcessed',
    });
  }
}

/**
 * Équivalent SSE de {@link showImportReportFromProcessedDatasource}, pour un import
 * lancé depuis cet onglet et dont le signal `DEAL` vient d'annoncer
 * l'aboutissement.
 *
 * Le toast "en cours" est **réservé avant la relecture**, pas résolu après : le
 * réserver le sort tout de suite des attentes, donc un second signal arrivant
 * pendant l'aller-retour — ou le `SYNC` d'une reconnexion — ne relance ni la
 * requête ni un second `openImportReport`, ce qui rouvrirait le rapport que
 * l'utilisateur vient de fermer et écraserait ses ré-inclusions. La référence
 * conservée permet malgré tout de remplacer le toast par un succès quand la source
 * n'a pas de rapport d'import.
 *
 * Aucun repli sur un nouveau toast, contrairement au chemin WebSocket : ici
 * l'identité de l'import vient de la réservation elle-même, donc l'absence de
 * réservation signifie « déjà résolu », jamais « lancé par quelqu'un d'autre ».
 */
export async function resolveAwaitedProcessedDatasource(
  client: ApolloClient,
  dealId: string,
  datasourceId: string,
  t: TFunction
): Promise<void> {
  const claimedToast = claimDealItemsInProgressToast({
    datasourceId,
    kind: 'datasourceProcessed',
  });
  if (!claimedToast) return;

  let dataSource: DealDataSource | undefined;
  try {
    const { data } = await client.query<GetDatasourceQuery>({
      query: GetDatasourceDocument,
      variables: { dealId },
      fetchPolicy: 'network-only',
    });
    dataSource = (data?.deal?.dataSources ?? []).find(
      (candidate) => candidate.id === datasourceId
    );
  } catch {
    dataSource = undefined;
  }

  if (dataSource && hasImportReport(dataSource)) {
    claimedToast.dismiss();
    openImportReport(dealId, dataSource);
    return;
  }

  claimedToast.resolveWithSuccess(datasourceCompletedToast(t));
}
